import axios from 'axios';
import {env} from 'next-runtime-env';

// ---------------------------------------------------------------------------
// Internal gateway identifier — hardcoded, never exposed in UI
// ---------------------------------------------------------------------------
const GATEWAY_ID = 'MERCHANT_GATEWAY';

const STORAGE_KEYS = {
  gateway: 'pj_gateway',
  openId: 'pj_merchant_open_id',
  embedded: 'pj_embedded',
} as const;

// ---------------------------------------------------------------------------
// Merchant backend client
// ---------------------------------------------------------------------------
function getMerchantApiUrl(): string {
  return env('NEXT_PUBLIC_MERCHANT_API_URL') || '';
}

function merchantClient() {
  return axios.create({baseURL: `${getMerchantApiUrl()}/api/v1`});
}

// ---------------------------------------------------------------------------
// Detection
// ---------------------------------------------------------------------------

/** Parse the `?code=` param from the current URL (set by the gateway redirect). */
export function parseMerchantCode(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  return params.get('code') || null;
}

/** Whether the current session came from the merchant gateway. */
export function isMerchantGateway(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(STORAGE_KEYS.gateway) === GATEWAY_ID;
}

/** Whether the app is running inside the gateway iframe. */
export function isEmbedded(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(STORAGE_KEYS.embedded) === 'true';
}

/** Get the stored merchant open_id. */
export function getMerchantOpenId(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(STORAGE_KEYS.openId);
}

// ---------------------------------------------------------------------------
// Auth — exchange code for identity
// ---------------------------------------------------------------------------

export interface MerchantLoginResult {
  openId: string;
  playerToken: string;
}

/**
 * Exchange the one-time authorisation code with the merchant backend.
 *
 * The merchant backend will:
 *   1. Call the gateway API to exchange `code` → `open_id`
 *   2. Register / login the player on the poker backend
 *   3. Return `{ open_id, playerToken }` to the frontend
 */
export async function exchangeCode(code: string): Promise<MerchantLoginResult> {
  const client = merchantClient();
  const {data: resp} = await client.post('/merchant/auth/login', {code});

  // Go service wraps result in { success, data, message }
  const data = resp.data || resp;

  if (!data.open_id) {
    throw new Error(resp.error || resp.message || 'Merchant login failed');
  }

  // Persist merchant session
  localStorage.setItem(STORAGE_KEYS.gateway, GATEWAY_ID);
  localStorage.setItem(STORAGE_KEYS.openId, data.open_id);
  localStorage.setItem(STORAGE_KEYS.embedded, 'true');

  // Store gateway JWT for gateway API access (fund-txn, etc.)
  console.log('[merchant-gateway] exchangeCode response data:', data);
  if (data.gatewayToken) {
    console.log('[merchant-gateway] Storing gatewayToken:', data.gatewayToken);
    localStorage.setItem('gatewayToken', data.gatewayToken);
  } else {
    console.warn('[merchant-gateway] No gatewayToken in response');
  }

  return {
    openId: data.open_id,
    playerToken: data.playerToken || data.player_token,
  };
}

/**
 * Restore a previously-authenticated merchant session.
 * Returns the open_id if present, or null.
 */
export function restoreSession(): string | null {
  if (!isMerchantGateway()) return null;
  return getMerchantOpenId();
}

/** Clear the merchant gateway session. */
export function clearMerchantSession(): void {
  Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
}

// ---------------------------------------------------------------------------
// Deposit & Withdraw — via merchant backend + postMessage
// ---------------------------------------------------------------------------

/**
 * Create a deposit (recharge) order via the merchant backend, then notify
 * the gateway parent iframe so it can show its confirmation dialog.
 */
export async function requestDeposit(openId: string, amount: string): Promise<string> {
  const client = merchantClient();
  const {data} = await client.post('/merchant/recharge', {
    open_id: openId,
    amount,
  });

  if (data.code !== 0) {
    throw new Error(data.message || 'Deposit order failed');
  }

  const orderNo: string = data.data.order_no;

  // Notify gateway parent to show the deposit confirmation UI
  window.parent.postMessage({type: 'recharge', order_no: orderNo}, '*');

  return orderNo;
}

/**
 * Create a withdraw order via the merchant backend, then notify the
 * gateway parent iframe.
 */
export async function requestWithdraw(openId: string, amount: string): Promise<string> {
  const client = merchantClient();
  const {data} = await client.post('/merchant/withdraw', {
    open_id: openId,
    amount,
  });

  if (data.code !== 0) {
    throw new Error(data.message || 'Withdraw order failed');
  }

  const orderNo: string = data.data.order_no;

  // Notify gateway parent to show the withdraw confirmation UI
  window.parent.postMessage({type: 'withdraw', order_no: orderNo}, '*');

  return orderNo;
}

/**
 * Confirm a deposit order after 88x parent sends recharge_result success.
 *
 * Gateway (Go) will:
 *   1. Verify the order is completed on 88x side
 *   2. Call game backend POST /v2/player/deposit-money to credit the poker wallet
 */
export async function confirmDeposit(openId: string, orderNo: string): Promise<void> {
  const client = merchantClient();
  const {data} = await client.post('/merchant/recharge/confirm', {
    open_id: openId,
    order_no: orderNo,
  });
  if (data.code !== 0) {
    throw new Error(data.message || 'Deposit confirm failed');
  }
}

/**
 * Confirm a withdraw order after 88x parent sends withdraw_result success.
 *
 * Gateway (Go) will:
 *   1. Verify the order is completed on 88x side
 *   2. Deduct from poker wallet on game backend
 */
export async function confirmWithdraw(openId: string, orderNo: string): Promise<void> {
  const client = merchantClient();
  const {data} = await client.post('/merchant/withdraw/confirm', {
    open_id: openId,
    order_no: orderNo,
  });
  if (data.code !== 0) {
    throw new Error(data.message || 'Withdraw confirm failed');
  }
}

/**
 * Fetch the user's miniapp balance from the merchant backend.
 */
export async function getMerchantBalance(openId: string): Promise<string> {
  const client = merchantClient();
  const {data} = await client.get('/merchant/balance', {params: {open_id: openId}});
  if (data.code !== 0) throw new Error(data.message || 'Balance query failed');
  return data.data.balance;
}

// ---------------------------------------------------------------------------
// PostMessage listener — results from gateway parent
// ---------------------------------------------------------------------------

export interface GatewayResult {
  success: boolean;
  orderNo?: string;
  message?: string;
}

export interface GatewayMessageCallbacks {
  onDepositResult?: (result: GatewayResult) => void;
  onWithdrawResult?: (result: GatewayResult) => void;
}

/**
 * Install a message listener for deposit/withdraw results from the gateway
 * parent iframe.
 *
 * Returns a cleanup function (call on unmount).
 */
export function onGatewayMessages(callbacks: GatewayMessageCallbacks): () => void {
  function handler(event: MessageEvent) {
    const msg = event.data;
    if (!msg || typeof msg !== 'object') return;

    if (msg.type === 'recharge_result' && callbacks.onDepositResult) {
      callbacks.onDepositResult({
        success: msg.success,
        orderNo: msg.order_no,
        message: msg.message,
      });
    }

    if (msg.type === 'withdraw_result' && callbacks.onWithdrawResult) {
      callbacks.onWithdrawResult({
        success: msg.success,
        orderNo: msg.order_no,
        message: msg.message,
      });
    }
  }

  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}

// ---------------------------------------------------------------------------
// Direct Deposit & Withdraw — via sub-account API (no iframe needed)
// ---------------------------------------------------------------------------

/**
 * Directly deposit: deducts from 88x miniapp balance, credits poker wallet.
 */
export async function directDeposit(openId: string, amount: string): Promise<void> {
  const client = merchantClient();
  const {data} = await client.post('/merchant/deposit/direct', {
    open_id: openId,
    amount,
  });

  if (data.code === 1004) {
    throw new Error('Insufficient USD balance');
  }
  if (data.code !== 0) {
    throw new Error(data.message || 'Deposit failed');
  }
}

/**
 * Directly withdraw: deducts from poker wallet, adds to 88x miniapp balance.
 */
export async function directWithdraw(openId: string, amount: string): Promise<void> {
  const client = merchantClient();
  const {data} = await client.post('/merchant/withdraw/direct', {
    open_id: openId,
    amount,
  });

  if (data.code !== 0) {
    throw new Error(data.message || 'Withdraw failed');
  }
}
